import {Map as ImMap} from 'immutable';
import {isImmutable} from '@packages/utilities/imm4-backports';
import {
  Json,
  getString,
  getNumber,
  getObject,
  getArray,
  getBoolean,
} from '@packages/utilities/json';
import {Component, Plugins} from '@packages/systems/wfdl';
import {
  EElement,
  EList,
  ERecord,
  EText,
  type Expression,
} from '@packages/systems/wfdl-ast';
import {qualify} from '@packages/systems/wfdl-Qualified';
import {
  LINK_TYPES,
  State as LinkData,
} from '@packages/shared/render/plugins/Basic/dataTypes/Link/LinkTypes';
import {
  DefaultValue,
  SerializeInput,
  PropSettings,
  CodeComponentProp,
} from './types';
import {PropType} from './constants';
import {incrementGuid} from '@packages/utilities/guid';

export function assertNever(_: never) {}

export function asJson(value: SerializeInput): Json | undefined {
  if (value === undefined) return value;
  return (
    isImmutable(value) ? (value as ImMap<string, unknown>).toJS() : value
  ) as Json;
}

export function toJson(value: DefaultValue): Json | undefined {
  try {
    if (value === undefined) return undefined;
    if (typeof value === 'boolean') return value;
    return JSON.parse(value);
  } catch (e) {
    return undefined;
  }
}

/**
 * Helper function to parse a string from a JSON value or undefined
 */
export function parseString<T = string>(value: SerializeInput): T | undefined {
  const jsonValue = asJson(value);
  return jsonValue !== undefined ? (getString(jsonValue) as T) : undefined;
}

/**
 * Helper function to parse a number from a JSON value or undefined
 */
export function parseNumber<T = number>(value: SerializeInput): T | undefined {
  const jsonValue = asJson(value);
  return jsonValue !== undefined ? (getNumber(jsonValue) as T) : undefined;
}

/**
 * Helper function to parse a link from a JSON value or undefined
 */
export function parseLink(value: SerializeInput) {
  const jsonValue = asJson(value);
  const data = jsonValue ? getObject(jsonValue) : undefined;
  if (!data) {
    return undefined;
  }
  const mode = parseString<LINK_TYPES>(data.mode);
  if (!mode) {
    return undefined;
  }

  const baseLink = {
    target: parseString<LinkData['target']>(data.target),
    preload: parseString<LinkData['preload']>(data.preload),
  };

  switch (mode) {
    case LINK_TYPES.EXTERNAL: {
      const url = parseString(data.url);
      return {...baseLink, mode, url};
    }
    case LINK_TYPES.EMAIL: {
      const email = parseString(data.email);
      const subject = parseString(data.subject);
      return {...baseLink, mode, email, subject};
    }
    case LINK_TYPES.TELEPHONE: {
      const tel = parseString<LinkData['tel']>(data.tel);
      return {...baseLink, mode, tel};
    }
    case LINK_TYPES.SECTION: {
      const domId = parseString<LinkData['domId']>(data.domId);
      return {...baseLink, mode, domId};
    }
    case LINK_TYPES.PAGE: {
      const pageId = parseString<LinkData['pageId']>(data.pageId);
      return {...baseLink, mode, pageId};
    }
    case LINK_TYPES.DYNPAGE: {
      const pageSlug = parseString<LinkData['pageSlug']>(data.pageSlug);
      return {...baseLink, mode, pageSlug};
    }
    case LINK_TYPES.FILE: {
      const assetId = parseString<LinkData['assetId']>(data.assetId);
      const assetUrl = parseString<LinkData['assetUrl']>(data.assetUrl);
      return {...baseLink, mode, assetId, assetUrl};
    }
    case LINK_TYPES.POPOVER:
    case LINK_TYPES.MODAL: {
      // TODO: preserve Popover/Modal Link semantics in code component props (https://linear.app/webflow/issue/DES-7847/preserve-popover-link-semantics-in-code-component-props-and-devlink)
      return {...baseLink, mode};
    }
    default: {
      assertNever(mode);
      return undefined;
    }
  }
}

/**
 * Helper function to parse an image from a JSON value or undefined
 */
export function parseImage(value: SerializeInput) {
  const jsonValue = asJson(value);
  const data = jsonValue ? getObject(jsonValue) : undefined;
  if (!data) {
    return undefined;
  }
  return {
    id: parseString(data.id),
    url: parseString(data.url),
    alt: parseString(data.alt),
  };
}

export function parseBoolean(value: SerializeInput): boolean | undefined {
  const jsonValue = asJson(value);
  if (!jsonValue) return undefined;

  // Handle the structure { tag: 'True' | 'False', value: {} }
  const data = getObject(jsonValue);
  if (data) {
    const tag = parseString(data.tag);
    if (tag === 'True') return true;
    if (tag === 'False') return false;
  }
  return undefined;
}

/**
 * Helper function to parse an array from a JSON value or undefined
 */
export function parseArray(value: SerializeInput) {
  const jsonValue = asJson(value);
  return jsonValue !== undefined ? getArray(jsonValue) : undefined;
}

export function parseRichTextDefaultValue(
  text: string,
  initialId: string
): Expression {
  let count = 0;
  const nextId = () => incrementGuid(initialId, ++count);
  return EList([
    EElement({
      id: nextId(),
      type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
      data: ERecord({
        children: EList([
          EElement({
            id: nextId(),
            type: qualify(Plugins.name('Basic'), Component.name('String')),
            data: EText(text),
          }),
        ]),
      }),
    }),
  ]);
}

/**
 * Prefix used for prop IDs to avoid collision with restricted prop names
 */
const PROP_ID_PREFIX = 'w-prop--';
const PROP_ID_TYPE_SEPARATOR = '--';

export function makePropId(prop: CodeComponentProp): string | null {
  if (!prop.name) return null;
  return `${PROP_ID_PREFIX}${prop.name}${PROP_ID_TYPE_SEPARATOR}${prop.type}`;
}

export function parsePropId(propId: string): string {
  const withoutPrefix = propId.replace(PROP_ID_PREFIX, '');
  const parts = withoutPrefix.split(PROP_ID_TYPE_SEPARATOR);
  const type = parts[parts.length - 1];
  if (type && type in PropType) {
    return parts.slice(0, -1).join(PROP_ID_TYPE_SEPARATOR);
  }

  return withoutPrefix;
}

/**
 * Helper function to parse prop settings from a stringified JSON value
 */
export function parsePropSettings(
  settings: string | undefined,
  propType: PropType
): PropSettings[PropType] | undefined {
  const json = toJson(settings);
  if (!json) return undefined;
  const object = getObject(json);
  if (!object) return undefined;

  switch (propType) {
    case PropType.Variant: {
      const options = parseArray(object.options);
      if (!options || options.some((option) => typeof option !== 'string'))
        return undefined;
      return {
        options: Array.from(new Set(options)),
      } as PropSettings[PropType.Variant];
    }
    case PropType.Number: {
      const min = parseNumber(object.min);
      const max = parseNumber(object.max);
      const decimals = parseNumber(object.decimals);

      // Validate settings
      if (decimals !== undefined && !Number.isInteger(decimals))
        // Decimals must be an integer
        return undefined;
      if (min !== undefined && max !== undefined && min > max)
        // Min must be less than max
        return undefined;

      return {
        ...(min !== undefined ? {min} : {}),
        ...(max !== undefined ? {max} : {}),
        ...(decimals !== undefined ? {decimals} : {}),
      } as PropSettings[PropType.Number];
    }
    case PropType.Boolean: {
      const trueLabel = parseString(object.trueLabel);
      const falseLabel = parseString(object.falseLabel);
      return {
        ...(trueLabel !== undefined ? {trueLabel} : {}),
        ...(falseLabel !== undefined ? {falseLabel} : {}),
      } as PropSettings[PropType.Boolean];
    }
    case PropType.TextNode: {
      const multiLine =
        object.multiline !== undefined
          ? getBoolean(object.multiline)
          : undefined;
      return {
        ...(multiLine !== undefined ? {multiLine} : {}),
      } as PropSettings[PropType.TextNode];
    }
    default: {
      return undefined;
    }
  }
}

const NON_BREAKING_SPACE = ' ';
const replaceAllSpaces = (spaces: string) =>
  spaces.replace(/ /g, NON_BREAKING_SPACE);

export function parseTextNodeDefaultValue(
  text: string,
  initialId: string
): Expression {
  let count = 0;
  const nextId = () => incrementGuid(initialId, ++count);
  // Here we replace some spaces with non-breaking spaces
  const chunks = text
    // Case 1: Whenever we have more than one space in a row, we need them to be
    // non-breaking spaces:
    .replace(/  +/g, replaceAllSpaces)
    // Case 2: Whenever we have a space at the beginning of a line, we need it
    // to be a non-breaking space:
    //
    // NOTE: Order of these matter! If we were to run this replacement first,
    // the first two spaces of a line will not be reflected as non-breaking.
    .replace(/^ /g, NON_BREAKING_SPACE)
    .replace(/\n /g, '\n' + NON_BREAKING_SPACE)
    .split('\n');
  const result: Expression[] = [];
  for (let i = 0; i < chunks.length; i += 1) {
    const value = chunks[i];
    if (value !== undefined) {
      result.push(
        EElement({
          id: nextId(),
          type: qualify(Plugins.name('Basic'), Component.name('String')),
          data: EText(value),
        })
      );
    }

    if (i !== chunks.length - 1) {
      result.push(
        EElement({
          id: nextId(),
          type: qualify(Plugins.name('Basic'), Component.name('LineBreak')),
          data: ERecord({}),
        })
      );
    }
  }
  return EList(result);
}
